import {createAsyncThunk} from '@reduxjs/toolkit';
import api, {CustomAxiosConfig} from 'services/axios';

const CPC_API_BASE_PATH = '/dasht/cpc';

type ApiEnvelope<T> = {
  data: T;
  error?: string;
  ok?: boolean;
};

type VendorCpcParams = {
  vendor_id: string | number;
};

type PreorderPlan = {
  min_price: number;
  max_price: number;
  min_click: number;
  max_click: number;
  click_buckets: number[];
  tax_rate: number;
};

type PreorderPlanCreateParams = {
  vendor_id: string | number;
  start_date: Date | string;
  cost_per_click: number;
  demand_click_count: number;
};

type ConfirmCpcOtpParams = VendorCpcParams & {
  code: string;
};

type ConfirmPreorderOutput = {
  renewal: boolean;
  start_date: string;
  state: string;
  tax_rate: number;
};

type CpcListParams = VendorCpcParams & {
  page: number;
  page_size: number;
};

type CpcCampaignParams = VendorCpcParams & {
  id: string | number;
};

type PauseCpcCampaignParams = CpcCampaignParams & {
  hold: boolean;
};

type CpcListItem = {
  id: number;
  start_date: string;
  end_date: string;
  demand_click_count: number;
  hold: boolean;
  state: string;
};

type CpcList = {
  size: number;
  total_items: number;
  total_pages: number;
  page: number;
  cpcs: CpcListItem[];
};

type CpcDetails = {
  id: number;
  state: string;
  start_date: string;
  end_date: string;
  active_click_count: number;
  demand_click_count: number;
  hold: boolean;
  used_budget: number;
  unused_budget: number;
  transaction_code: string;
  payment_id: number;
  payment_price: number;
  payment_tax_rate: number;
  payment_total_price: number;
};

export const preorderPlanGet = createAsyncThunk<ApiEnvelope<PreorderPlan>, VendorCpcParams>(
  'preorderPlanGet',
  async (data, thunkApi) => {
    try {
      const response = await api.get(`${CPC_API_BASE_PATH}/preorder/plan?vendor_id=${data.vendor_id}`, {
        goBaseUrl: true
      } as CustomAxiosConfig);
      return thunkApi.fulfillWithValue(response.data);
    } catch (e: any) {
      return thunkApi.rejectWithValue(e);
    }
  }
);

export const preorderPlanCreate = createAsyncThunk<
  ApiEnvelope<Record<string, never>>,
  PreorderPlanCreateParams
>('preorderPlanCreate', async (data, thunkApi) => {
  try {
    const response = await api.post(`${CPC_API_BASE_PATH}/preorder?vendor_id=${data.vendor_id}`, data, {
      goBaseUrl: true
    } as CustomAxiosConfig);
    return thunkApi.fulfillWithValue(response.data);
  } catch (e: any) {
    return thunkApi.rejectWithValue(e);
  }
});

export const cpcList = createAsyncThunk<ApiEnvelope<CpcList>, CpcListParams>(
  'cpcList',
  async (params, thunkApi) => {
    try {
      const response = await api.get(CPC_API_BASE_PATH, {
        params,
        goBaseUrl: true
      } as CustomAxiosConfig);
      return thunkApi.fulfillWithValue(response.data);
    } catch (e: any) {
      return thunkApi.rejectWithValue(e);
    }
  }
);

export const cpcGet = createAsyncThunk<ApiEnvelope<CpcDetails>, CpcCampaignParams>(
  'cpcGet',
  async (params, thunkApi) => {
    try {
      const response = await api.get(`${CPC_API_BASE_PATH}/${params.id}`, {
        params,
        goBaseUrl: true
      } as CustomAxiosConfig);
      return thunkApi.fulfillWithValue(response.data);
    } catch (e: any) {
      return thunkApi.rejectWithValue(e);
    }
  }
);

export const confirmCPCOTP = createAsyncThunk<
  ApiEnvelope<ConfirmPreorderOutput>,
  ConfirmCpcOtpParams
>('confirmCPCOTP', async (data, thunkApi) => {
  try {
    const response = await api.put(`${CPC_API_BASE_PATH}/preorder?vendor_id=${data.vendor_id}`, data, {
      goBaseUrl: true
    } as CustomAxiosConfig);
    return thunkApi.fulfillWithValue(response.data);
  } catch (e: any) {
    return thunkApi.rejectWithValue(e);
  }
});

export const deleteCPCCampaign = createAsyncThunk<
  ApiEnvelope<Record<string, never>>,
  CpcCampaignParams
>('deleteCPCCampaign', async (params, thunkApi) => {
  try {
    const response = await api.delete(
      `${CPC_API_BASE_PATH}/${params.id}?vendor_id=${params.vendor_id}`,
      {
        goBaseUrl: true
      } as CustomAxiosConfig
    );
    return thunkApi.fulfillWithValue(response.data);
  } catch (e: any) {
    return thunkApi.rejectWithValue(e);
  }
});

export const renewalCPCCampaign = createAsyncThunk<
  ApiEnvelope<Record<string, never>>,
  CpcCampaignParams
>('renewalCPCCampaign', async (data, thunkApi) => {
  try {
    const response = await api.put(
      `${CPC_API_BASE_PATH}/${data.id}/renewal?vendor_id=${data.vendor_id}`,
      data,
      {
        goBaseUrl: true
      } as CustomAxiosConfig
    );
    return thunkApi.fulfillWithValue(response.data);
  } catch (e: any) {
    return thunkApi.rejectWithValue(e);
  }
});

export const pauseCPCCampaign = createAsyncThunk<
  ApiEnvelope<Record<string, never>>,
  PauseCpcCampaignParams
>('pauseCPCCampaign', async (data, thunkApi) => {
  try {
    const response = await api.put(
      `${CPC_API_BASE_PATH}/${data.id}/pause?vendor_id=${data.vendor_id}`,
      data,
      {
        goBaseUrl: true
      } as CustomAxiosConfig
    );
    return thunkApi.fulfillWithValue(response.data);
  } catch (e: any) {
    return thunkApi.rejectWithValue(e);
  }
});
